import { useState } from "react";
import "./SplashPages.css";

interface SplashPageProps {
  type:
    | "SPLASH_1"
    | "SPLASH_2"
    | "SPLASH_3"
    | "SPLASH_4"
    | "SPLASH_COMMITTED"
    | "SPLASH_LANDLORD";
  onNewsletterOptIn?: () => void;
}

export default function SplashPage({
  type,
  onNewsletterOptIn,
}: SplashPageProps) {
  const [optedIn, setOptedIn] = useState(false);

  const handleOptIn = () => {
    setOptedIn(true);
    onNewsletterOptIn?.();
  };

  // SPLASH_1 — Early exit (CAS1 secondary / first gateway No)
  if (type === "SPLASH_1") {
    return (
      <div className="splash-container">
        <div className="splash-content">
          <div className="splash-logo">LOA</div>
          <h2>Thanks for stopping by.</h2>
          <p className="splash-body">
            We're building something we think the world needs. If you ever want
            to come back, the door's open.
          </p>
          <div className="splash-divider" />
          <p className="splash-newsletter-text">
            Want to stay in the loop? We'll only email you when there's
            something worth sharing.
          </p>
          {!optedIn ? (
            <button className="splash-opt-in" onClick={handleOptIn}>
              Yes, keep me updated
            </button>
          ) : (
            <div className="splash-confirmed">
              <span className="confirmed-check">✓</span>
              You're on the list. We'll be in touch.
            </div>
          )}
          <p className="splash-data-note">
            Your data from this session will be deleted.
          </p>
          <div className="splash-close">
            <button
              className="splash-close-btn"
              onClick={() => {
                window.location.href = "https://getloa.netlify.app";
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SPLASH_2 — Failed both gateways
  if (type === "SPLASH_2") {
    return (
      <div className="splash-container">
        <div className="splash-content">
          <div className="splash-logo">LOA</div>
          <h2>Thanks for sharing your thoughts.</h2>
          <p className="splash-body">
            It genuinely helps - every response shapes what we're building. LOA
            is still taking shape, and when it's ready, we think you'll feel it.
          </p>
          <div className="splash-divider" />
          <p className="splash-newsletter-text">Stay in the loop?</p>
          {!optedIn ? (
            <button className="splash-opt-in" onClick={handleOptIn}>
              Yes, keep me updated
            </button>
          ) : (
            <div className="splash-confirmed">
              <span className="confirmed-check">✓</span>
              You're on the list. We'll be in touch.
            </div>
          )}
          <p className="splash-data-note">
            Your survey responses will be anonymised. Personal data deleted.
          </p>
          <div className="splash-close">
            <button
              className="splash-close-btn"
              onClick={() => {
                window.location.href = "https://getloa.netlify.app";
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SPLASH_3 — Declined pre-booking
  if (type === "SPLASH_3") {
    return (
      <div className="splash-container">
        <div className="splash-content">
          <div className="splash-logo">LOA</div>
          <h2>You made it this far. That means something.</h2>
          <p className="splash-body">
            We get it - committing before it exists takes courage. But you
            clearly feel what we're building. When we launch, you'll be the
            first to know.
          </p>
          <div className="splash-divider" />
          <p className="splash-newsletter-text">
            Want to hear from us when it's real?
          </p>
          {!optedIn ? (
            <button className="splash-opt-in" onClick={handleOptIn}>
              Yes, keep me updated
            </button>
          ) : (
            <div className="splash-confirmed">
              <span className="confirmed-check">✓</span>
              You're on the list. We'll be in touch.
            </div>
          )}
          <p className="splash-data-note">
            No data will be stored beyond your email.
          </p>
          <div className="splash-close">
            <button
              className="splash-close-btn"
              onClick={() => {
                window.location.href = "https://getloa.netlify.app";
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SPLASH_4 — Pre-booked but declined investment
  if (type === "SPLASH_4") {
    return (
      <div className="splash-container splash-4">
        <div className="splash-content">
          <div className="splash-logo">LOA</div>
          <h2>Investing in businesses isn't for most people.</h2>
          <p className="splash-body splash-emphasis">
            But investing in yourself? That's always sensible.
          </p>
          <p className="splash-body">
            Thank you so much for committing to us - you are going to love the
            journey we're going on. Your founding member status means you'll be
            right there with us as we build, test, and open the doors.
          </p>
          <p className="splash-body">
            The investment opportunity isn't going anywhere - it'll be waiting
            in your member dashboard whenever the timing feels right. No
            pressure. No expiry.
          </p>
          <p className="splash-body splash-warm">
            For now, we're just stoked to have you on board. See you at your
            first session. 🚀
          </p>
          <div className="splash-divider" />
          <p className="splash-data-note">
            You'll receive an email with your login details shortly.
          </p>
        </div>
      </div>
    );
  }

  // SPLASH_COMMITTED — CBS2 or CBS3 committed (founding member end state)
  if (type === "SPLASH_COMMITTED") {
    return (
      <div className="splash-container splash-committed">
        <div className="splash-content">
          <div className="splash-logo">LOA</div>
          <div className="committed-icon">🙌</div>
          <h2>You're a founding member.</h2>
          <p className="splash-body splash-emphasis">
            This is the beginning of something worth being part of.
          </p>
          <p className="splash-body">
            Thank you - genuinely. You've just done something most people won't:
            backed a vision before it became obvious. That's what founders do.
          </p>
          <p className="splash-body">
            As a founding member, you'll be among the first through the doors
            when we open. You'll get priority access, founding member pricing,
            and a front-row seat as we build this.
          </p>
          <p className="splash-body splash-warm">
            You'll receive an email with your app login details shortly. Until
            then - we'll see you very soon. 🚀
          </p>
          <div className="splash-divider" />
          <p className="splash-data-note">
            You can close this window. We've got your details.
          </p>
        </div>
      </div>
    );
  }

  // SPLASH_LANDLORD — End of landlord session
  if (type === "SPLASH_LANDLORD") {
    return (
      <div
        style={{
          minHeight: "100dvh",
          background: "#0a0a0a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "1.1rem",
            fontWeight: 600,
            letterSpacing: "0.5em",
            color: "#E8544F",
          }}
        >
          L O A
        </div>
      </div>
    );
  }

  return null;
}
